import type { PublicJwk } from "@atrium/shared";
import type { Identity } from "./crypto";

/*
 * Device-local storage for end-to-end encryption, in IndexedDB. Private keys
 * are stored as non-extractable CryptoKey objects: they survive reloads, but
 * page script can only use them, never read the key material out.
 */

const DB = "atrium-e2ee";
const STORE = "kv";

/** A key we've seen for someone (trust on first use), optionally verified by comparing safety numbers. */
export interface Pin {
  fingerprint: string;
  verified: boolean;
  /** Every signing key we've trusted for them, newest last, so older messages still verify after a reset. */
  signingKeys: PublicJwk[];
}

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  return (dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = null;
      reject(req.error);
    };
  }));
}

async function tx<T>(mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = run(t.objectStore(STORE));
    t.oncomplete = () => resolve(req.result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

const get = <T>(key: string) => tx<T | undefined>("readonly", (s) => s.get(key) as IDBRequest<T | undefined>);
const put = (key: string, value: unknown) => tx("readwrite", (s) => s.put(value, key));
const del = (key: string) => tx("readwrite", (s) => s.delete(key));

export const loadIdentity = (userId: string) => get<Identity>(`identity:${userId}`);
export const saveIdentity = (userId: string, identity: Identity) => put(`identity:${userId}`, identity);
export const forgetIdentity = (userId: string) => del(`identity:${userId}`);

export const loadPins = async (userId: string) => (await get<Record<string, Pin>>(`pins:${userId}`)) ?? {};
export const savePins = (userId: string, pins: Record<string, Pin>) => put(`pins:${userId}`, pins);

/**
 * Signing out removes every private key from this device (they come back from
 * the backup on next unlock). Pins stay: they're public fingerprints, and
 * keeping them is what lets a later key swap be noticed.
 */
export async function wipeKeys() {
  const keys = await tx<IDBValidKey[]>("readonly", (s) => s.getAllKeys());
  await Promise.all(keys.filter((k) => typeof k === "string" && k.startsWith("identity:")).map((k) => del(k as string)));
}
